// JOBE-0106: ProjectHomePage — top-level route component for
// `/projects/{urn}`.
//
// Owns:
//   - the project detail fetch (`GET /api/projects/{urn}?expand=rollups`)
//   - the members fetch (`GET /api/projects/{urn}/members`)
//   - the `?tab=` URL state (Decision 2)
//   - the roster fetch for the chip composer's PeopleValuePopover
//   - the Reassign-owner modal state
//
// Spec: specs/in-progress/JOBE-0106-projects-home-route.md
// ADR-0023 rollout plan, Phase 5.

import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useProjectDetail } from './useProjectDetail.js';
import { useRoster } from './useRoster.js';
import { ProjectHeader } from './header/ProjectHeader.jsx';
import { AskBar } from './AskBar.jsx';
import { KPIStrip } from './KPIStrip.jsx';
import { SubTabNav, SUB_TABS } from './SubTabNav.jsx';
import { OverviewBody } from './overview/OverviewBody.jsx';
import { TasksTab } from './tabs/TasksTab.jsx';
import { DocumentsTab } from './tabs/DocumentsTab.jsx';
import { EntitiesTab } from './tabs/EntitiesTab.jsx';
import { ActivityTab } from './tabs/ActivityTab.jsx';
import { SettingsTab } from './tabs/SettingsTab.jsx';
import { ReassignOwnerModal } from './ReassignOwnerModal.jsx';
import { SideRail } from './rail/SideRail.jsx';

import './projects.css';

const VALID_TABS = new Set(SUB_TABS.map((t) => t.name));

// useRoster moved to ./useRoster.js (JOBE-0116) for shared use by
// /projects/{urn} (this page) and /projects/{urn}/entities/{kind}.

function ProjectHomePageLoading() {
  return (
    <div className="project-home-page project-home-loading" data-testid="project-home-loading">
      <div className="project-home-skeleton-header" />
      <div className="project-home-skeleton-kpi" />
      <div className="project-home-skeleton-nav" />
      <p className="muted">Loading project…</p>
    </div>
  );
}

function ProjectHomePageCedarDenied() {
  return (
    <div className="project-home-page project-home-denied" data-testid="project-home-denied">
      <div className="card project-home-denied-card">
        <p>You don&apos;t have access to this project.</p>
        <p>
          <a href="/projects" className="link">← Back to projects</a>
        </p>
      </div>
    </div>
  );
}

function ProjectHomePageError({ error, onRetry }) {
  return (
    <div className="project-home-page project-home-error" data-testid="project-home-error">
      <div className="card project-home-error-card">
        <p>
          Couldn&apos;t load project
          {error?.status ? ` (status ${error.status})` : ''}.
        </p>
        <button
          type="button"
          className="project-home-retry"
          onClick={onRetry}
          data-testid="project-home-retry"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

export function ProjectHomePage() {
  const { urn: rawUrn } = useParams();
  const projectURN = useMemo(() => {
    if (!rawUrn) return '';
    try { return decodeURIComponent(rawUrn); } catch { return rawUrn; }
  }, [rawUrn]);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabRaw = searchParams.get('tab') || 'overview';
  const activeTab = VALID_TABS.has(tabRaw) ? tabRaw : 'overview';

  const { project, members, loadState, error, retry } = useProjectDetail(projectURN);
  const roster = useRoster();

  const [reassignOpen, setReassignOpen] = useState(false);
  const openReassign = useCallback(() => setReassignOpen(true), []);
  const closeReassign = useCallback(() => setReassignOpen(false), []);
  const onReassignCommitted = useCallback(() => {
    setReassignOpen(false);
    retry();
  }, [retry]);

  const onSelectTab = useCallback((next) => {
    if (!VALID_TABS.has(next)) return;
    const merged = new URLSearchParams(searchParams);
    if (next === 'overview') {
      merged.delete('tab');
    } else {
      merged.set('tab', next);
    }
    setSearchParams(merged, { replace: false });
  }, [searchParams, setSearchParams]);

  const openSettings = useCallback(() => {
    onSelectTab('settings');
  }, [onSelectTab]);

  if (loadState === 'loading') return <ProjectHomePageLoading />;
  if (loadState === 'cedar-denied') return <ProjectHomePageCedarDenied />;
  if (loadState === 'error') return <ProjectHomePageError error={error} onRetry={retry} />;

  const counts = project?.counts || project?.rollups || null;
  const currentOwnerURN = members?.assignee || null;

  return (
    <div className="project-home-page" data-testid="project-home-page">
      <header className="project-home-titlebar">
        <h1 className="project-home-title">
          {project?.name || project?.urn || projectURN}
        </h1>
        {project?.summary || project?.lede ? (
          <p className="project-home-lede muted">
            {project.summary || project.lede}
          </p>
        ) : null}
      </header>

      <div className="project-home-layout" data-testid="project-home-layout">
        <main className="project-home-main">
          <ProjectHeader
            project={project}
            members={members}
            counts={counts}
            onReassign={openReassign}
          />

          <AskBar project={project} counts={counts} />

          <KPIStrip counts={counts} />

          <SubTabNav
            activeTab={activeTab}
            counts={counts}
            onSelectTab={onSelectTab}
          />

          <div className="project-home-tab-body" data-testid="project-home-tab-body">
            {activeTab === 'overview' && (
              <OverviewBody projectURN={projectURN} counts={counts} />
            )}
            {activeTab === 'tasks' && (
              <TasksTab projectURN={projectURN} roster={roster} />
            )}
            {activeTab === 'documents' && (
              <DocumentsTab projectURN={projectURN} roster={roster} />
            )}
            {activeTab === 'entities' && (
              <EntitiesTab projectURN={projectURN} roster={roster} />
            )}
            {activeTab === 'activity' && <ActivityTab />}
            {activeTab === 'settings' && <SettingsTab project={project} />}
          </div>
        </main>

        <SideRail
          projectURN={projectURN}
          detail={project}
          onOpenSettings={openSettings}
        />
      </div>

      {reassignOpen ? (
        <ReassignOwnerModal
          projectURN={projectURN}
          currentOwnerURN={currentOwnerURN}
          roster={roster}
          onClose={closeReassign}
          onCommitted={onReassignCommitted}
        />
      ) : null}
    </div>
  );
}
